import { createPortal } from 'react-dom';

import { CalendarIcon } from './internal/icons';
import { DateBoxPopover } from './internal/DateBoxPopover';
import { FieldAffix } from './internal/FieldAffix';
import { PopoverSurface } from './internal/PopoverSurface';
import { useDateBox } from './internal/useDateBox';
import type { DateBoxProps } from './types';

/**
 * The date engine (design's `DateField`): a text input + a "wheels" calendar popover, driven by
 * `mode` (single/range) and `withTime`. Controlled by a canonical string value; emits `onChange`
 * only on committed, coherent values (single on pick; range once both ends or an ∞ end are set).
 * The named presets `DateRangeBox` / `DateTimeBox` / `DateTimeRangeBox` lock these switches.
 *
 * State, handlers and the popover live in `useDateBox` / `DateBoxPopover`; this component just wires
 * them into the shared field chrome and portals the popover above the desktop windows.
 */
export function DateBox(props: DateBoxProps) {
  const c = useDateBox(props);

  return (
    <>
      <FieldAffix
        {...c.chrome}
        inputId={c.inputId}
        leadingIcon={<CalendarIcon />}
        placeholder={c.placeholder}
        text={c.inputText}
        invalid={c.invalid || Boolean(c.chrome.error)}
        error={c.errorText}
        open={c.open}
        triggerAriaLabel={c.openCalendarLabel}
        anchorRef={c.anchorRef}
        inputRef={c.inputRef}
        onAffixMouseDown={c.onAffixMouseDown}
        onInputChange={c.onInputChange}
        onInputFocus={c.onInputFocus}
        onInputBlur={c.onInputBlur}
        onInputKeyDown={c.onInputKeyDown}
        onToggle={c.onToggle}
      />
      {c.open
        ? createPortal(
            <PopoverSurface
              ref={c.popRef}
              positionStyle={c.popStyle}
              data-testid="date-box-popover"
              onMouseDown={(event) => {
                // Klik do kalendáře nesmí rozostřit input (jinak by se blur-commit rozepsaného
                // textu spustil dřív než výběr dne a přepsal ho). Vstup necháme dostat focus.
                if (!(event.target instanceof HTMLInputElement)) event.preventDefault();
              }}
            >
              <DateBoxPopover c={c} />
            </PopoverSurface>,
            document.body
          )
        : null}
    </>
  );
}
